import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { PermissionsService } from '../shared/services/permissions.service';

/**
 * Gate the `/settings` lazy module so admins always pass through AND
 * non-admins with at least one module permission grant can enter the
 * settings shell to reach their permitted pages.
 *
 * Previously this route used `AdminGuard`, which silently defeated the
 * per-user permission system: a non-admin coach with a Read grant on
 * `manage_calendar` would be bounced at this parent route, never reaching
 * the child `modulePermissionGuard` that would actually let them in. The
 * forensic audit caught this; this guard is the documented replacement.
 *
 * Behaviour:
 *   - admin / super_admin → always pass (Rule 1/3 in PermissionsService)
 *   - non-admin with at least one Read or Write grant → pass (then each
 *     child route's `modulePermissionGuard` filters which pages they see)
 *   - everyone else → redirect to `/`
 *
 * Awaits `permissions.whenLoaded()` so it doesn't race the cold-load
 * hydration (same fix the child `modulePermissionGuard` applies).
 */
export const settingsEntryGuard: CanActivateFn = async () => {
  const permissions = inject(PermissionsService);
  const router = inject(Router);

  await permissions.whenLoaded();

  // Admin/super_admin always allowed (matches the historical `AdminGuard`).
  if (permissions.isAdmin()) return true;

  // Non-admins are allowed in only if they have at least one module
  // permission grant (Read or Write). `visibleModuleKeys` already
  // applies the resolution rules and feature-flag short-circuit.
  if (permissions.visibleModuleKeys().size > 0) return true;

  router.navigate(['/']);
  return false;
};
